import React, { useEffect, useState } from "react";
import {
  X,
  Sparkles,
  Brain,
  Compass,
  Target,
  ExternalLink,
  Image as ImageIcon,
  Play,
  Lock,
  Copy,
  Loader2,
} from "lucide-react";
import { message } from "antd";
import { STATUSES } from "../services/theWebData";
import { showsHookRate } from "./WebGraph";
import { useCreativeActions } from "../lib/creativeActions";
import { briefLockReason } from "../lib/confidenceGating";
import AIService from "../services/AIService";
import { generateBrief, runBrainScan } from "../services/creativeIntel";

/**
 * AdDetailModal
 *
 * Click-to-popup view for any node (concept, variation, hook). Bigger
 * surface than the side panel — includes a creative preview, full
 * performance grid, AI thesis, lineage strip, and action buttons.
 *
 * Closes on:
 *   - X button click
 *   - backdrop click
 *   - Escape key
 *
 * Props:
 *   node: the selected graph node (concept | variation | hook) or null
 *   onClose: () => void
 *   onSelect: (id) => void  — used by lineage chips to swap the modal
 *   getChildren, getParent, getRelatedHooks: lineage helpers
 *   allNodes, allEdges: for the hook view
 */
export default function AdDetailModal({
  node,
  onClose,
  onSelect,
  getChildren,
  getParent,
  getRelatedHooks,
  allNodes,
  allEdges,
  context = null,
}) {
  // close on Escape
  useEffect(() => {
    if (!node) return;
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [node, onClose]);

  if (!node) return null;

  const isHook = node.type === "hook";
  const isUnexplored = node.status === "unexplored";

  return (
    <div
      className="web-modal__backdrop"
      onClick={onClose}
      role="presentation"
    >
      <div
        className="web-modal"
        role="dialog"
        aria-modal="true"
        aria-label={node.label}
        onClick={(e) => e.stopPropagation()}
      >
        <button className="web-modal__close" onClick={onClose} aria-label="Close">
          <X size={16} />
        </button>

        {isHook ? (
          <HookModalBody node={node} allEdges={allEdges} allNodes={allNodes} onSelect={onSelect} />
        ) : (
          <CreativeModalBody
            node={node}
            isUnexplored={isUnexplored}
            getChildren={getChildren}
            getParent={getParent}
            getRelatedHooks={getRelatedHooks}
            onSelect={onSelect}
            context={context}
          />
        )}
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------- */
/*  Creative (concept / variation) body                                  */
/* -------------------------------------------------------------------- */

const CreativeModalBody = ({
  node,
  isUnexplored,
  getChildren,
  getParent,
  getRelatedHooks,
  onSelect,
  context,
}) => {
  const parent = getParent?.(node.id);
  const children = getChildren?.(node.id) || [];
  const hooks = getRelatedHooks?.(node.id) || [];

  const {
    briefOpen,
    closeBrief,
    brainOpen,
    closeBrain,
    briefLocked,
    handleBrief,
    handleSendToBrain,
    handleReferenceWinners,
    handleAddToTestQueue,
  } = useCreativeActions(node);
  const confidence = node.confidence || 0;

  return (
    <div className="web-modal__body">
      {/* Preview hero */}
      <div className="web-modal__hero">
        <CreativePreview node={node} />
        <div className="web-modal__hero-meta">
          <div className="web-modal__kind">
            {node.type === "concept" ? "CONCEPT" : "VARIATION"}
            {node.scaling ? " · SCALING" : ""}
            {isUnexplored ? " · UNEXPLORED" : ""}
          </div>
          <h2 className="web-modal__title">{node.label}</h2>
          <div className="web-modal__chips">
            {node.funnel ? (
              <span className={`web-detail__chip web-detail__chip--${node.funnel}`}>
                {node.funnel.toUpperCase()}
              </span>
            ) : null}
            {node.awareness ? (
              <span className="web-detail__chip">{node.awareness}-aware</span>
            ) : null}
            {node.angle ? <span className="web-detail__chip">{node.angle}</span> : null}
            {node.format ? (
              <span className="web-detail__chip">
                {String(node.format).replace(/_/g, " ")}
              </span>
            ) : null}
            {node.scaling ? (
              <span className="web-detail__chip web-detail__chip--scale">scaling</span>
            ) : null}
            <span className="web-detail__chip">
              {(STATUSES[node.status] || STATUSES.active).label}
            </span>
          </div>
          {node.thesis ? (
            <div className="web-modal__thesis-quote">{node.thesis}</div>
          ) : null}
        </div>
      </div>

      {/* Performance grid */}
      {!isUnexplored && (
        <section className="web-modal__section">
          <div className="web-modal__section-label">Performance</div>
          <div className="web-modal__perf">
            <PerfCell label="Spend" value={`$${(node.spend || 0).toLocaleString()}`} />
            <PerfCell
              label="ROAS"
              value={(node.roas || 0).toFixed(2)}
              tone={
                (node.roas || 0) >= 4 ? "gold" :
                (node.roas || 0) >= 3 ? "good" :
                (node.roas || 0) < 2 && (node.spend || 0) > 200 ? "bad" : ""
              }
            />
            <PerfCell label="CTR" value={`${(node.ctr || 0).toFixed(2)}%`} />
            {showsHookRate(node) ? (
              <PerfCell
                label="Hook rate"
                value={`${Math.round((node.hookRate || 0) * 100)}%`}
              />
            ) : null}
            <PerfCell
              label="Confidence"
              value={`${Math.round((node.confidence || 0) * 100)}%`}
            />
            <PerfCell
              label="Status"
              value={(STATUSES[node.status] || STATUSES.active).label}
              small
            />
          </div>
        </section>
      )}

      {/* AI thesis */}
      {!isUnexplored && (
        <section className="web-modal__section">
          <div className="web-modal__section-label">AI thesis · element responsibility</div>
          <div className="web-thesis" style={{ fontStyle: "normal" }}>
            <strong style={{ color: "var(--w-fg)" }}>The single idea:</strong>{" "}
            {dnaThesis(node, "idea")}
            <br />
            <strong style={{ color: "var(--w-fg)" }}>What made it land:</strong>{" "}
            {dnaThesis(node, "mechanism")}
            <br />
            <strong style={{ color: "var(--w-fg)" }}>Most likely cause:</strong>{" "}
            {dnaThesis(node, "lever")}
          </div>
        </section>
      )}

      {/* Lineage strip */}
      {(parent || children.length > 0 || hooks.length > 0) && (
        <section className="web-modal__section">
          <div className="web-modal__section-label">Lineage</div>
          <div className="web-modal__lineage">
            {parent ? (
              <LineageChip
                kind="parent"
                node={parent}
                onClick={() => onSelect(parent.id)}
              />
            ) : null}
            {children.map((c) => (
              <LineageChip
                key={c.id}
                kind={c.scaling ? "scaling" : "child"}
                node={c}
                onClick={() => onSelect(c.id)}
              />
            ))}
            {hooks.map((h) => (
              <LineageChip
                key={h.id}
                kind="hook"
                node={h}
                onClick={() => onSelect(h.id)}
              />
            ))}
          </div>
        </section>
      )}

      {/* Actions */}
      <section className="web-modal__section">
        <div className="web-modal__section-label">Actions</div>
        <div className="web-modal__actions">
          <button
            type="button"
            className="web-btn web-btn--gold"
            onClick={handleBrief}
            disabled={briefLocked}
            title={
              briefLocked
                ? briefLockReason(node)
                : "Generate a variation brief from this creative"
            }
          >
            {briefLocked ? <Lock size={14} /> : <Sparkles size={14} />}
            {briefLocked ? "Brief locked · low confidence" : "Generate variation brief"}
          </button>
          {!isUnexplored ? (
            <>
              <button type="button" className="web-btn" onClick={handleSendToBrain}>
                <Brain size={14} /> Send to Brain
              </button>
              {node._meta?.ad_id ? (
                <a
                  className="web-btn"
                  href={`https://www.facebook.com/ads/library/?id=${node._meta.ad_id}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <ExternalLink size={14} /> Open in Ads Library
                </a>
              ) : null}
            </>
          ) : (
            <>
              <button
                type="button"
                className="web-btn"
                onClick={handleReferenceWinners}
              >
                <Compass size={14} /> Reference winners
              </button>
              <button
                type="button"
                className="web-btn"
                onClick={handleAddToTestQueue}
              >
                <Target size={14} /> Add to test queue
              </button>
            </>
          )}
        </div>
      </section>

      {briefOpen ? (
        <BriefDraftModal
          node={node}
          parent={parent}
          onClose={closeBrief}
          context={context}
        />
      ) : null}

      {brainOpen ? (
        <BrainScanModal node={node} onClose={closeBrain} context={context} />
      ) : null}
    </div>
  );
};

/* -------------------------------------------------------------------- */
/*  BriefDraftModal — placeholder until AIService.generateBrief lands     */
/* -------------------------------------------------------------------- */

export const BriefDraftModal = ({ node, parent, onClose, context }) => {
  const [status, setStatus] = useState("running"); // running | done | error
  const [step, setStep] = useState("Starting…");
  const [markdown, setMarkdown] = useState("");
  const [brief, setBrief] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    const controller = new AbortController();
    let cancelled = false;

    const run = async () => {
      setStatus("running");
      setError("");
      try {
        const aiService = new AIService({
          ...(context?.apiKeys || {}),
          workspaceId: context?.workspaceId || null,
          aiProfile: context?.aiProfile || null,
          analysisKnowledge: context?.analysisKnowledge || null,
        });
        const result = await generateBrief({
          node,
          aiService,
          workspaceId: context?.workspaceId || null,
          model: context?.analysisModel || "",
          signal: controller.signal,
          onProgress: (s) => {
            if (!cancelled) setStep(s);
          },
        });
        if (cancelled) return;
        setBrief(result.brief);
        setMarkdown(result.markdown);
        setStatus("done");
      } catch (err) {
        if (cancelled || controller.signal.aborted) return;
        setError(err?.message || "Brief generation failed");
        setStatus("error");
      }
    };

    run();
    return () => {
      cancelled = true;
      controller.abort();
    };
    // Regenerate only when the target node changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [node?.id]);

  const handleCopy = () => {
    if (!markdown) return;
    navigator.clipboard?.writeText(markdown).then(
      () => message.success({ content: "Brief copied to clipboard.", duration: 2 }),
      () => message.error({ content: "Could not copy. Select + ⌘C the text instead.", duration: 4 }),
    );
  };

  return (
    <div className="web-modal__nested-backdrop" onClick={onClose} role="presentation">
      <div className="web-modal__nested" onClick={(e) => e.stopPropagation()}>
        <button className="web-modal__close" onClick={onClose} aria-label="Close brief">
          <X size={16} />
        </button>
        <div className="web-modal__section-label" style={{ marginBottom: 8 }}>
          Variation brief{status === "done" && brief?.usedKnowledge ? ` · cites ${brief.usedKnowledge} KB chunks` : ""}
        </div>

        {status === "running" ? (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
              padding: "28px 8px",
              color: "var(--w-muted)",
              fontSize: 13,
            }}
          >
            <Loader2 size={16} style={{ animation: "spin 1s linear infinite" }} />
            {step}
          </div>
        ) : null}

        {status === "error" ? (
          <div style={{ fontSize: 12, color: "#f87171", marginBottom: 14, whiteSpace: "pre-wrap" }}>
            {error}
          </div>
        ) : null}

        {status === "done" ? (
          <>
            <div style={{ fontSize: 12, color: "var(--w-muted)", marginBottom: 14 }}>
              Generated from the creative's script and its watch-through curve — drop-off points
              diagnosed, script adjusted, and learnings saved to your knowledge base.
            </div>
            <pre
              style={{
                padding: 16,
                background: "rgba(0,0,0,0.4)",
                border: "1px solid var(--w-border)",
                borderRadius: 12,
                fontFamily: "var(--w-mono)",
                fontSize: 12,
                lineHeight: 1.6,
                color: "var(--w-fg-dim)",
                whiteSpace: "pre-wrap",
                margin: 0,
                maxHeight: "52vh",
                overflow: "auto",
              }}
            >
{markdown}
            </pre>
          </>
        ) : null}

        <div style={{ display: "flex", gap: 8, marginTop: 14, justifyContent: "flex-end" }}>
          <button type="button" className="web-btn" onClick={onClose}>
            Close
          </button>
          <button
            type="button"
            className="web-btn web-btn--gold"
            onClick={handleCopy}
            disabled={status !== "done"}
          >
            <Copy size={14} /> Copy brief
          </button>
        </div>
      </div>
    </div>
  );
};

/* -------------------------------------------------------------------- */
/*  BrainScanModal — inline "Send to Brain": script + frames + neural scan */
/* -------------------------------------------------------------------- */

const SECTION_LABEL_STYLE = {
  fontSize: 11,
  textTransform: "uppercase",
  letterSpacing: "0.06em",
  color: "var(--w-muted)",
  marginBottom: 6,
};

export const BrainScanModal = ({ node, onClose, context }) => {
  const [status, setStatus] = useState("running"); // running | done | error
  const [step, setStep] = useState("Starting…");
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    const controller = new AbortController();
    let cancelled = false;

    (async () => {
      setStatus("running");
      setError("");
      try {
        const aiService = new AIService({
          ...(context?.apiKeys || {}),
          workspaceId: context?.workspaceId || null,
          aiProfile: context?.aiProfile || null,
          analysisKnowledge: context?.analysisKnowledge || null,
        });
        const res = await runBrainScan({
          node,
          aiService,
          workspaceId: context?.workspaceId || null,
          signal: controller.signal,
          onProgress: (s) => {
            if (!cancelled) setStep(s);
          },
        });
        if (cancelled) return;
        setResult(res);
        setStatus("done");
      } catch (err) {
        if (cancelled || controller.signal.aborted) return;
        setError(err?.message || "Brain scan failed");
        setStatus("error");
      }
    })();

    return () => {
      cancelled = true;
      controller.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [node?.id]);

  const script = result?.script;
  const frames = result?.frames || [];
  const interp = result?.interpretation;
  const tribe = result?.tribe;
  const timestepImages = Array.isArray(tribe?.timestepImages) ? tribe.timestepImages : [];
  const hasScript =
    script && (script.transcript || script.hook || script.onScreenText?.length || script.scenes?.length);

  return (
    <div className="web-modal__nested-backdrop" onClick={onClose} role="presentation">
      <div className="web-modal__nested" onClick={(e) => e.stopPropagation()}>
        <button className="web-modal__close" onClick={onClose} aria-label="Close brain scan">
          <X size={16} />
        </button>
        <div className="web-modal__section-label" style={{ marginBottom: 8 }}>
          <Brain size={14} style={{ verticalAlign: "-2px", marginRight: 6 }} />
          Send to Brain · {node.label}
        </div>

        {status === "running" ? (
          <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "28px 8px", color: "var(--w-muted)", fontSize: 13 }}>
            <Loader2 size={16} style={{ animation: "spin 1s linear infinite" }} />
            {step}
          </div>
        ) : null}

        {status === "error" ? (
          <div style={{ fontSize: 12, color: "#f87171", marginBottom: 14, whiteSpace: "pre-wrap" }}>{error}</div>
        ) : null}

        {status === "done" ? (
          <div style={{ display: "flex", flexDirection: "column", gap: 18, maxHeight: "62vh", overflow: "auto" }}>
            {/* Neural response */}
            {interp ? (
              <section>
                <div style={SECTION_LABEL_STYLE}>Neural response</div>
                {interp.overall ? (
                  <p style={{ fontSize: 13, color: "var(--w-fg)", margin: "0 0 8px" }}>{interp.overall}</p>
                ) : null}
                {interp.advertiserInsights ? (
                  <p style={{ fontSize: 12, color: "var(--w-fg-dim)", margin: "0 0 8px" }}>
                    <strong style={{ color: "var(--w-fg)" }}>For the advertiser: </strong>
                    {interp.advertiserInsights}
                  </p>
                ) : null}
                {interp.consumerExperience ? (
                  <p style={{ fontSize: 12, color: "var(--w-fg-dim)", margin: 0 }}>
                    <strong style={{ color: "var(--w-fg)" }}>Viewer experience: </strong>
                    {interp.consumerExperience}
                  </p>
                ) : null}
                {interp.predictions?.length ? (
                  <div style={{ marginTop: 10, display: "flex", flexDirection: "column", gap: 6 }}>
                    {interp.predictions.map((p, i) => (
                      <div key={i} style={{ fontSize: 12 }}>
                        <strong style={{ color: "var(--w-fg)" }}>{p.metric}: </strong>
                        <span style={{ color: "var(--w-fg-dim)" }}>{p.prediction}</span>
                      </div>
                    ))}
                  </div>
                ) : null}
              </section>
            ) : tribe?.error ? (
              <section>
                <div style={SECTION_LABEL_STYLE}>Neural response</div>
                <p style={{ fontSize: 12, color: "var(--w-muted)", margin: 0 }}>
                  Neural scan unavailable ({tribe.error}). Script and frames were still extracted below.
                </p>
              </section>
            ) : null}

            {/* Timestep activation images */}
            {timestepImages.length ? (
              <section>
                <div style={SECTION_LABEL_STYLE}>Activation timeline</div>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(120px, 1fr))", gap: 6 }}>
                  {timestepImages.map((img, i) => (
                    <div key={i} style={{ background: "#000", borderRadius: 6, overflow: "hidden", textAlign: "center", padding: 3 }}>
                      <img
                        src={`data:${img.mimeType || "image/png"};base64,${img.base64}`}
                        alt={`t=${img.timestep}s`}
                        style={{ width: "100%", borderRadius: 4 }}
                      />
                      <span style={{ fontSize: 9, color: "#888" }}>t={img.timestep}s</span>
                    </div>
                  ))}
                </div>
              </section>
            ) : null}

            {/* Extracted script */}
            {hasScript ? (
              <section>
                <div style={SECTION_LABEL_STYLE}>Extracted script</div>
                {script.hook ? (
                  <p style={{ fontSize: 13, fontWeight: 600, color: "var(--w-fg)", margin: "0 0 6px" }}>
                    “{script.hook}”
                  </p>
                ) : null}
                {script.transcript ? (
                  <p style={{ fontSize: 12, color: "var(--w-fg-dim)", whiteSpace: "pre-wrap", margin: "0 0 8px" }}>
                    {script.transcript}
                  </p>
                ) : null}
                {script.onScreenText?.length ? (
                  <div style={{ marginBottom: 8 }}>
                    {script.onScreenText.map((t, i) => (
                      <div key={i} style={{ fontSize: 12, color: "var(--w-fg-dim)" }}>• {t}</div>
                    ))}
                  </div>
                ) : null}
                {script.scenes?.length ? (
                  <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                    {script.scenes.map((s, i) => (
                      <div key={i} style={{ borderLeft: "2px solid var(--w-border)", paddingLeft: 10 }}>
                        <span style={{ fontSize: 11, color: "var(--w-accent, #f5c451)" }}>{s.timeLabel || `Beat ${i + 1}`}</span>
                        {s.visual ? <div style={{ fontSize: 12, color: "var(--w-fg-dim)" }}>{s.visual}</div> : null}
                        {s.spoken ? <div style={{ fontSize: 12, color: "var(--w-muted)" }}>“{s.spoken}”</div> : null}
                      </div>
                    ))}
                  </div>
                ) : null}
              </section>
            ) : (
              <section>
                <div style={SECTION_LABEL_STYLE}>Extracted script</div>
                <p style={{ fontSize: 12, color: "var(--w-muted)", margin: 0 }}>
                  Couldn't read the script — the media bytes weren't reachable from the browser. Analyze
                  from an uploaded file to transcribe.
                </p>
              </section>
            )}

            {/* Key frames */}
            {frames.length ? (
              <section>
                <div style={SECTION_LABEL_STYLE}>Key frames</div>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(120px, 1fr))", gap: 6 }}>
                  {frames.map((f, i) => (
                    <div key={i} style={{ background: "#000", borderRadius: 6, overflow: "hidden", textAlign: "center", padding: 3 }}>
                      <img src={f.dataUrl} alt={f.label || `frame ${i + 1}`} style={{ width: "100%", borderRadius: 4 }} />
                      <span style={{ fontSize: 9, color: "#888" }}>{f.label || `frame ${i + 1}`}</span>
                    </div>
                  ))}
                </div>
              </section>
            ) : null}
          </div>
        ) : null}

        <div style={{ display: "flex", gap: 8, marginTop: 14, justifyContent: "flex-end" }}>
          <button type="button" className="web-btn" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
};

/* -------------------------------------------------------------------- */
/*  Hook body                                                           */
/* -------------------------------------------------------------------- */

const HookModalBody = ({ node, allEdges, allNodes, onSelect }) => {
  const grafted = (allEdges || [])
    .filter((e) => e.source === node.id && e.type === "cross_pollination")
    .map((e) => allNodes.find((n) => n.id === e.target))
    .filter(Boolean);

  return (
    <div className="web-modal__body">
      <div className="web-modal__hero">
        <div className="web-modal__preview web-modal__preview--hook">
          <div className="web-modal__hook-mark">"</div>
          <div className="web-modal__hook-quote">{node.label}</div>
        </div>
        <div className="web-modal__hero-meta">
          <div className="web-modal__kind">HOOK · REUSABLE</div>
          <h2 className="web-modal__title">{node.label}</h2>
          <div className="web-modal__chips">
            <span className="web-detail__chip">{node.archetype}</span>
            <span className="web-detail__chip">reuses · {node.reuses}</span>
            <span className="web-detail__chip">
              win rate · {Math.round((node.winRate || 0) * 100)}%
            </span>
          </div>
        </div>
      </div>

      <section className="web-modal__section">
        <div className="web-modal__section-label">Hook performance</div>
        <div className="web-modal__perf">
          <PerfCell
            label="Win rate"
            value={`${Math.round((node.winRate || 0) * 100)}%`}
            tone="gold"
          />
          <PerfCell label="Reuses" value={node.reuses} />
          <PerfCell label="Archetype" value={node.archetype} small />
        </div>
      </section>

      {grafted.length > 0 && (
        <section className="web-modal__section">
          <div className="web-modal__section-label">
            Grafted onto · {grafted.length}
          </div>
          <div className="web-modal__lineage">
            {grafted.map((g) => (
              <LineageChip
                key={g.id}
                kind={g.scaling ? "scaling" : "child"}
                node={g}
                onClick={() => onSelect(g.id)}
              />
            ))}
          </div>
        </section>
      )}

      <section className="web-modal__section">
        <div className="web-modal__section-label">Actions</div>
        <div className="web-modal__actions">
          <button className="web-btn web-btn--gold">
            <Sparkles size={14} /> Graft onto fatigued concept
          </button>
          <button className="web-btn">
            <ImageIcon size={14} /> Generate 5 visual variations
          </button>
        </div>
      </section>
    </div>
  );
};

/* -------------------------------------------------------------------- */
/*  Sub-pieces                                                          */
/* -------------------------------------------------------------------- */

const CreativePreview = ({ node }) => {
  if (node.thumbnail) {
    return (
      <div className="web-modal__preview">
        <img src={node.thumbnail} alt={node.label} />
        {node.format?.startsWith("video") ? (
          <span className="web-modal__preview-badge">
            <Play size={11} /> Video
          </span>
        ) : null}
      </div>
    );
  }

  // Procedural placeholder when there's no thumbnail (seed data / unexplored)
  const status = node.status || "active";
  const isVideo = node.format?.startsWith("video");
  return (
    <div
      className="web-modal__preview web-modal__preview--placeholder"
      data-status={status}
    >
      <div className="web-modal__preview-glyph">
        {isVideo ? <Play size={32} /> : <ImageIcon size={32} />}
      </div>
      <div className="web-modal__preview-label">
        {(node.format || "creative").replace(/_/g, " ")}
      </div>
    </div>
  );
};

const PerfCell = ({ label, value, tone, small }) => (
  <div className={`web-modal__perf-cell ${small ? "is-small" : ""}`}>
    <div className="web-modal__perf-k">{label}</div>
    <div className={`web-modal__perf-v ${tone ? `is-${tone}` : ""}`}>{value}</div>
  </div>
);

const LineageChip = ({ kind, node, onClick }) => {
  const labelByKind = {
    parent: "PARENT",
    scaling: "SCALING",
    child: (node.type || "var").slice(0, 3).toUpperCase(),
    hook: "HOOK",
  };
  const metric = node.type === "hook"
    ? `${Math.round((node.winRate || 0) * 100)}%`
    : `${(node.roas || 0).toFixed(1)}×`;
  return (
    <button
      type="button"
      className={`web-modal__lineage-chip is-${kind}`}
      onClick={onClick}
    >
      <span className="web-modal__lineage-kind">{labelByKind[kind] || ""}</span>
      <span className="web-modal__lineage-name">{node.label}</span>
      <span className="web-modal__lineage-metric">{metric}</span>
    </button>
  );
};

/* -------------------------------------------------------------------- */
/*  Hand-crafted thesis lines for the seed; procedural fallback otherwise */
/* -------------------------------------------------------------------- */

function dnaThesis(n, slot) {
  const m = {
    c_wife_notices: {
      idea: "Grooming as relational care, not vanity.",
      mechanism: "Wife-POV opens a private outcome the buyer wants but won't admit.",
      lever: "Hook (text-screenshot specificity).",
    },
    v_wn_v2c: {
      idea: "Make the headline carry the proof.",
      mechanism: "Specificity ('14 days') closes the credibility gap on a fear product.",
      lever: "Headline craft — visual is unchanged from v2.",
    },
    c_founder_letter: {
      idea: "Authority + mechanism reveal.",
      mechanism: "Founder explains why drugstore tools fail skin below the belt.",
      lever: "Trust transfer (face + receipts).",
    },
    c_specificity_14d: {
      idea: "Concrete window beats vague promise.",
      mechanism: "'14 days' is verifiable — buyer can check themselves.",
      lever: "Headline + sticky-note format.",
    },
  };
  return (
    m[n.id]?.[slot] ||
    {
      idea: `${n.angle || "Angle"} · ${n.awareness || "problem"}-aware framing.`,
      mechanism: "Pattern interrupt + specific outcome promise in first 3s.",
      lever: "Hook archetype is doing the heavy lifting.",
    }[slot]
  );
}
